import { create } from '@bufbuild/protobuf'
import type { Transport } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation as useConnectMutation,
  useQuery as useConnectQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import type { GetConsentResponse } from '@jotta/grpc-connect-openapi/people'
import {
  Consents_ConsentState,
  GetConsentResponseSchema,
} from '@jotta/grpc-connect-openapi/people'
import {
  getConsent,
  updateConsent,
} from '@jotta/grpc-connect-openapi/peopleQuery'

const consentStaleTime = 1000 * 60 * 2

function peopleConsentQueryKey(transport: Transport) {
  return createConnectQueryKey({
    schema: getConsent,
    input: {},
    transport,
    cardinality: 'finite',
  })
}

function selectAllowFaceGroupingConsent(response: GetConsentResponse): boolean {
  return response.consents?.allowFaceGrouping === Consents_ConsentState.YES
}

export function useFaceGroupingConsentQuery({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const transport = useTransport()

  return useConnectQuery(
    getConsent,
    {},
    {
      transport,
      enabled,
      staleTime: consentStaleTime,
      select: selectAllowFaceGroupingConsent,
    },
  )
}

export function useUpdateFaceGroupingConsent() {
  const transport = useTransport()
  const queryClient = useQueryClient()

  return useConnectMutation(updateConsent, {
    transport,
    // A refetch already in flight resolves with the pre-mutation value and
    // would overwrite the cache write below, silently reverting the toggle.
    onMutate: () =>
      queryClient.cancelQueries({ queryKey: peopleConsentQueryKey(transport) }),
    onSuccess: response => {
      queryClient.setQueryData(
        peopleConsentQueryKey(transport),
        create(GetConsentResponseSchema, {
          consents: response.consents,
        }),
      )
    },
  })
}
